import React from "react"

import {
	Divider,
	List,
	ListItemButton,
	Typography
} from "@mui/material"

import { Trans } from "@lingui/macro"

import { ActionDetail } from "components/actions/action-detail"

import { ErrorResponse, RestResponse } from "@sinossi/mates-react-library"
import { Action, AssignableUsers, AssignRequest, SessionResponse } from "models"
import { WorkflowService } from "services/workflow.service"
import withUseRouter, { UseRouterParams } from "../../../services/router"
import { MatesComponent, MatesComponentState } from "../../mates-component"
import { AssignWorkflowAction, DeferAction } from "../../workflow/modals"
import { MarkActionAsDone } from "../../workflow/modals/mark-action-as-done"
import { SkipActionDialog } from "../../workflow/modals/skip-action-dialog"
import ShowActionDetails from "./show-action-details"

interface DashboardActionsListProps extends UseRouterParams {
	actions: Action[]
	sessionData: SessionResponse
	refresh: () => void
}

interface DashboardActionsListState extends MatesComponentState {
	openDefer: boolean
	openDone: boolean
	openDetails: boolean
	openAssign: boolean
	openSkip: boolean
	action: Action | null
	assignableUsers: AssignableUsers[]
}

class DashboardActionsList extends React.Component<DashboardActionsListProps, DashboardActionsListState> {
	private readonly matesComponent: MatesComponent<DashboardActionsListProps, DashboardActionsListState> = new MatesComponent(this)

	constructor(props: DashboardActionsListProps) {
		super(props);
		this.state = {
			...this.matesComponent.setupState(),
			openDefer: false,
			openDone: false,
			openDetails: false,
			openAssign: false,
			openSkip: false,
			assignableUsers: [],
			action: null
		}
	}

	private openDeferDialog = (action: Action) => {
		this.setState(() => ({
			openDefer: true,
			action: action
		}))
	}

	private closeDeferDialog = () => {
		this.setState(() => ({
			openDefer: false,
			action: null
		}))
	}

	private submitDefer = (values: any) => {
		if (!this.state.action) {
			return
		}

		const request: { deadline: string } = { deadline: values.deadline }

		WorkflowService.deferAction(this.state.action.id, request, this.submitDeferCallback, this.matesComponent.onException)
	}

	private submitDeferCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}
		this.closeDeferDialog()
		this.props.refresh()
	}

	private openMarkAsDone = (action: Action) => {
		this.setState(() => ({
			openDone: true,
			action: action
		}))
	}

	private closeMarkAsDone = () => {
		this.setState(() => ({
			openDone: false,
			action: null
		}))
	}

	private submitDone = () => {
		if (!this.state.action) {
			return
		}

		WorkflowService.markAsDone(this.state.action.id, this.submitDoneCallback, this.matesComponent.onException)
	}

	private submitDoneCallback = (response: RestResponse<any, ErrorResponse>) => {

		this.closeMarkAsDone()

		this.props.refresh()
	}

	private openDetails = (action: Action) => {
		this.setState(() => ({
			action: action,
			openDetails: true
		}))
	}

	private closeDetails = () => {
		this.setState(() => ({
			action: null,
			openDetails: false
		}))
	}

	private openAssignDialog = (action: Action) => {
		this.setState(() => ({
			action: action,
			openAssign: true,
			loading: true
		}), () => this.getAssignableUsers(action.id))
	}

	private closeAssignDialog = () => {
		this.setState(() => ({
			action: null,
			openAssign: false
		}))
	}

	private assignAction = (actionId: string, userId: string) => {
		let request: AssignRequest = {
			executor: userId
		}
		WorkflowService.assignWorkflowAction(actionId, request, this.assignWorkflowActionCallback, this.matesComponent.onError)
	}

	private assignWorkflowActionCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.closeAssignDialog()
		this.props.refresh()
	}

	private getAssignableUsers = (actionId: string) => {
		//WorkflowService.getAssignableUsers(actionId, this.getAssignableUsersCallback, this.matesComponent.onException)
	}

	private getAssignableUsersCallback = (response: RestResponse<AssignableUsers[], ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}
		if (response.data) {
			this.setState(() => ({
				assignableUsers: response.data!,
				loading: false
			}))
		}
	}

	private openSkipDialog = (action: Action) => {
		this.setState(() => ({
			openSkip: true,
			action: action
		}))
	}

	private closeSkipDialog = () => {
		this.setState(() => ({
			openSkip: false,
			action: null
		}))
	}

	private skipAction = () => {
		this.setState(() => ({
			loading: true
		}), () => WorkflowService.skipAction(this.state.action!.id, this.skipActionCallback, this.matesComponent.onError))
	}

	private skipActionCallback = (response: RestResponse<any, ErrorResponse>) => {

		this.closeSkipDialog()
		this.props.refresh()
	}

	private scrollToLastElem = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		const htmlElem = (event.target as HTMLElement).parentElement as HTMLElement
		const listElem = (htmlElem.parentElement as HTMLElement).previousElementSibling as HTMLElement
		const listItemsElem = Array.from(listElem.children).filter((elem) => elem.classList.contains("MuiButtonBase-root"))
		const targetListItem = listItemsElem[listItemsElem.length - 1] as HTMLElement
		targetListItem.scrollIntoView({
			block: "end",
			inline: "nearest",
			behavior: "smooth"
		})
	}

	render() {
		const { actions } = this.props

		return (
			<>
				{actions.length === 0 &&
					<Typography sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
						<Trans id="dashboard.no-actions-to-do">
							Nessuna azione da fare
						</Trans>
					</Typography>
				}

				<List sx={{ width: "100%", height: `100%`, overflow: "auto", mt: 4 }}>
					{actions.map((data: any) => (
						<>
							<ListItemButton key={data.id} disableRipple disableTouchRipple>
								<ActionDetail data={data}
									sessionData={this.props.sessionData}
									openDeferDialog={() => this.openDeferDialog(data)}
									openMarkAsDone={() => this.openMarkAsDone(data)}
									openAssignDialog={() => this.openAssignDialog(data)}
									openSkipDialog={() => this.openSkipDialog(data)}
									onClick={() => this.openDetails(data)}
								/>
							</ListItemButton>
							<Divider />
						</>
					))}
				</List>
				{
					this.state.openDefer && this.state.action &&
					<DeferAction openDialog
						onClose={this.closeDeferDialog}
						submit={this.submitDefer}
						data={this.state.action}
					/>
				}

				{
					this.state.openDone && this.state.action &&
					<MarkActionAsDone onClose={this.closeMarkAsDone}
						submit={this.submitDone}
						action={this.state.action}
					/>
				}
				{
					this.state.openDetails && this.state.action &&
					<ShowActionDetails action={this.state.action}
						onClose={this.closeDetails}
						refresh={this.props.refresh} />
				}
				{
					this.state.openAssign && this.state.action &&
					<AssignWorkflowAction onClose={this.closeAssignDialog}
						openDialog={this.state.openAssign}
						data={this.state.action}
						assign={this.assignAction}
						users={[]}
					/>
				}
				{
					this.state.openSkip && this.state.action &&
					<SkipActionDialog onClose={this.closeSkipDialog}
						skip={this.skipAction}
					/>
				}
			</>
		)
	}
}

export default withUseRouter(DashboardActionsList)
